import {useState, useMemo} from 'react';
import {truncateRichContent, RichContent} from '@wix/ricos';
import {getRicoRichContent} from '../RichContentViewer/utils';
import {isEqual} from 'lodash';
import {FooterDescriptionTrimMode} from '../../../types/galleryTypes';

export type UseExpandableRichContentParams = {
  contentAsString: string;
  trimEnabled: boolean;
  trimMode: FooterDescriptionTrimMode;
  elementLimit: number;
  wordLimit: number;
};

export type UseExpandableRichContentResult = {
  content: RichContent | null;
  isExpanded: boolean;
  canBeTruncated: boolean;
  toggleExpansion: () => void;
};

export const useExpandableRichContent = ({
  contentAsString,
  trimEnabled,
  trimMode,
  elementLimit,
  wordLimit,
}: UseExpandableRichContentParams): UseExpandableRichContentResult => {
  const [isExpanded, setIsExpanded] = useState(!trimEnabled);

  const {fullContent, truncatedContent, canBeTruncated} = useMemo(() => {
    const isContentTooShortToTruncate = (content: RichContent): boolean => {
      if (trimMode !== FooterDescriptionTrimMode.Elements) {
        return false;
      }

      const elementCount = content.nodes?.length;
      return elementCount <= elementLimit;
    };

    const parsedContent = getRicoRichContent(contentAsString);

    if (!parsedContent || !trimEnabled) {
      return {
        fullContent: parsedContent,
        truncatedContent: parsedContent,
        canBeTruncated: false,
      };
    }

    if (isContentTooShortToTruncate(parsedContent)) {
      return {
        fullContent: parsedContent,
        truncatedContent: parsedContent,
        canBeTruncated: false,
      };
    }

    const truncateOptions =
      trimMode === FooterDescriptionTrimMode.Words ? {wordCount: wordLimit} : {nodeCount: elementLimit};

    const truncated = truncateRichContent(parsedContent, truncateOptions);

    const wasTruncated = !isEqual(truncated, parsedContent);

    return {
      fullContent: parsedContent,
      truncatedContent: truncated,
      canBeTruncated: wasTruncated,
    };
  }, [contentAsString, trimEnabled, trimMode, wordLimit, elementLimit]);

  const content = isExpanded ? fullContent : truncatedContent;

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  return {
    content,
    isExpanded,
    canBeTruncated,
    toggleExpansion,
  };
};
